import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { TextBox } from '@progress/kendo-react-inputs';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { Navigate, NavLink, Outlet, useMatch, useNavigate, useSearchParams } from 'react-router';
import { ChevronLeftOutline, CloseOutline, SearchOutline } from '../../../icons/icons';
import { SettingsSection } from './components/base/sections/SettingsSection';
import { sections } from './components/base/settings-definitions';
import { SettingsNavContext } from './context/SettingsNavContext';
import { SearchContext } from './context/SettingsSearchContext';
import './Settings.css';

export default function Settings() {
    const navigate = useNavigate();
    const isSearch = useMatch('/settings');
    const isSection = useMatch('/settings/:section');
    const isNested = useMatch('/settings/:section/:page');
    const [titleDiv, setTitleDiv] = React.useState<HTMLDivElement>();
    const [nestedWidth, setNestedWidth] = React.useState('');
    const contextValue = React.useMemo(() => ({ title: titleDiv, setPageWidth: setNestedWidth }), [titleDiv]);
    const [searchParams, setSearchParams] = useSearchParams();
    const searchTerm = searchParams.get('search') ?? '';
    const searchContextValue = React.useMemo(() => ({ term: isSearch ? searchTerm : '' }), [searchTerm, isSearch]);
    const lastSection = useRef<string>(undefined);
    const upOneLevel = isNested ? `/settings/${isNested.params.section}` : '/settings';
    if (isSection) {
        lastSection.current = isSection?.pathname;
    }

    const filtered = React.useMemo(() => {
        if (!isSearch) {
            return [];
        }
        const q = new RegExp(RegExp.escape(searchTerm), 'i');
        return sections
            .map((s) => ({
                ...s,
                groups: s.groups.map((g) =>
                    g.type === 'custom' || q.test(g.name)
                        ? g
                        : {
                              ...g,
                              elements: g.elements.filter((e) => {
                                  if ('hint' in e && e.hint && q.test(e.hint)) {
                                      return e;
                                  }
                                  if ('name' in e && e.name && q.test(e.name)) {
                                      return e;
                                  }
                                  if ('searchTag' in e && e.searchTag && q.test(e.searchTag)) {
                                      return e;
                                  }
                              })
                          }
                )
            }))
            .map((s) => ({ ...s, groups: s.groups.filter((g) => g.type !== 'custom' && g.elements.length > 0) }))
            .filter((s) => s.groups.length);
    }, [searchTerm, isSearch]);

    const handleSearchTextChanged = (text: string) => {
        if (isNested) {
            return;
        }

        if (!text) {
            navigate(lastSection.current ?? '/settings/general');
            return;
        }

        if (!isSearch) {
            navigate('/settings');
        }

        setSearchParams({ search: text });
    };

    if (isSearch && !searchTerm) {
        return <Navigate to={'/settings/general'} />;
    }

    return (
        <div className="h-100 d-flex gap-3 flex-shrink-0">
            <div style={{ width: 250, minWidth: 220 }}>
                <div className="px-3 p-2 settings-title">Settings</div>
                <div className="d-flex flex-column">
                    {sections.map((x) => (
                        <NavLink
                            to={x.path}
                            key={x.path}
                            className={(e) => clsx('d-flex align-items-center gap-2 ps-3 py-2 settings-link', e.isActive ? 'active' : '')}>
                            <SvgIcon icon={x.icon} />
                            {x.name}
                        </NavLink>
                    ))}
                </div>
            </div>

            {!isNested && (
                <div className="w-100 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
                    <div className="d-flex flex-column mx-auto" style={{ width: 'min(100%, 675px)' }}>
                        <div className="py-2 px-1 bg-color" style={{ position: 'sticky', top: 0, zIndex: 602 }}>
                            <TextBox
                                size={'large'}
                                placeholder="Search"
                                rounded={'full'}
                                value={searchTerm}
                                prefix={() => (
                                    <div className="d-inline-flex align-items-center ps-3">
                                        <SvgIcon icon={SearchOutline} />
                                    </div>
                                )}
                                suffix={() => (
                                    <div className="d-inline-flex align-items-center pe-1 ">
                                        <Button
                                            svgIcon={CloseOutline}
                                            fillMode={'flat'}
                                            className="k-rounded-full"
                                            onClick={() => handleSearchTextChanged('')}
                                        />
                                    </div>
                                )}
                                onChange={(e) => handleSearchTextChanged(e.value as string)}
                            />
                        </div>

                        <div className="pb-3">
                            <SearchContext.Provider value={searchContextValue}>
                                {isSearch ? (
                                    <>
                                        {searchTerm.length > 1 && filtered.length > 0 ? (
                                            filtered.map((x) => <SettingsSection key={x.path} section={x} />)
                                        ) : (
                                            <div className="secondary-text text-center my-3">No search results.</div>
                                        )}
                                    </>
                                ) : (
                                    <Outlet />
                                )}
                            </SearchContext.Provider>
                        </div>
                    </div>
                </div>
            )}

            {isNested && (
                <div className="w-100 min-w-0 h-100 d-flex justify-content-center">
                    <div className="h-100 mx-2 d-flex flex-column gap-2" style={{ width: nestedWidth }}>
                        <div className="d-flex gap-2 py-2">
                            <Button
                                svgIcon={ChevronLeftOutline}
                                fillMode={'flat'}
                                className="button-small"
                                onClick={() => navigate(upOneLevel)}
                            />
                            <div className="flex-grow-1" ref={(e) => setTitleDiv(e!)}></div>
                        </div>
                        <div className="h-100 min-h-0 mb-2">
                            <SettingsNavContext.Provider value={contextValue}>
                                <Outlet />
                            </SettingsNavContext.Provider>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
